import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import {
  useAllLifts,
  getMaintenanceStatusColor,
  type Lift,
} from '@/hooks';
import { useNotify } from '@/hooks/useNotify';
import { useFloorPlan, useUpdateFloorPlan } from '@/hooks/useTenantSettings';
import { useUpdateLiftPositions } from '@/hooks/useLifts';
import { FloorPlanCanvas } from './FloorPlanCanvas';

interface LiftPosition {
  id: string;
  x: number;
  y: number;
}

export function FloorPlanView() {
  const { t } = useTranslation('equipment');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: liftsData, isLoading: liftsLoading } = useAllLifts(true);
  const { floorPlanUrl, isLoading: settingsLoading } = useFloorPlan();
  const updateFloorPlan = useUpdateFloorPlan();
  const updatePositions = useUpdateLiftPositions();

  const [positions, setPositions] = useState<Map<string, { x: number; y: number }>>(new Map());
  const [isDirty, setIsDirty] = useState(false);

  const lifts = liftsData ?? [];

  // Initialize positions from lift data
  const liftsWithoutPositions = lifts.filter(
    (l) => l.floorPlanX == null && !positions.has(l.id)
  );

  const getPosition = useCallback((lift: Lift) => {
    const override = positions.get(lift.id);
    if (override) return override;
    if (lift.floorPlanX != null && lift.floorPlanY != null) {
      return { x: lift.floorPlanX, y: lift.floorPlanY };
    }
    return null;
  }, [positions]);

  const handlePositionChange = useCallback((liftId: string, x: number, y: number) => {
    setPositions((prev) => {
      const next = new Map(prev);
      next.set(liftId, { x, y });
      return next;
    });
    setIsDirty(true);
  }, []);

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      notify.error(tc('error'), t('floorPlan.imageTooLarge'));
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await updateFloorPlan.mutateAsync(reader.result as string);
      } catch (error) {
        notify.error(tc('error'), describeApiError(error, tc, { fallback: t('floorPlan.saveFailed') }));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSavePositions = async () => {
    if (positions.size === 0) return;

    const positionArray: LiftPosition[] = [];
    positions.forEach((pos, id) => {
      positionArray.push({ id, x: pos.x, y: pos.y });
    });

    try {
      await updatePositions.mutateAsync(positionArray);
      notify.success(t('floorPlan.saved'), t('floorPlan.savedDesc'));
      setIsDirty(false);
      setPositions(new Map());
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('floorPlan.saveFailed') }));
    }
  };

  if (liftsLoading || settingsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!floorPlanUrl) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {t('floorPlan.noFloorPlan')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('floorPlan.noFloorPlanDesc')}
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          style={{ display: 'none' }}
          onChange={(e) => { handleUpload(e); }}
        />
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={updateFloorPlan.isPending}
        >
          {t('floorPlan.uploadImage')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">{t('floorPlan.title')}</Typography>
        <Stack direction="row" spacing={1}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            style={{ display: 'none' }}
            onChange={(e) => { handleUpload(e); }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => fileInputRef.current?.click()}
            disabled={updateFloorPlan.isPending}
          >
            {t('floorPlan.changeImage')}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={() => void handleSavePositions()}
            disabled={!isDirty || updatePositions.isPending}
          >
            {updatePositions.isPending ? t('floorPlan.saving') : t('floorPlan.savePositions')}
          </Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {/* Floor plan canvas */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative',
            minHeight: 400,
          }}
        >
          <FloorPlanCanvas
            imageUrl={floorPlanUrl}
            lifts={lifts}
            getPosition={getPosition}
            onPositionChange={handlePositionChange}
          />
        </Paper>

        {/* Dock — unplaced lifts */}
        {liftsWithoutPositions.length > 0 && !positions.size && (
          <Paper
            elevation={0}
            sx={{
              width: { xs: '100%', md: 200 },
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {t('floorPlan.dock')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              {t('floorPlan.dragHint')}
            </Typography>
            <Stack spacing={1}>
              {liftsWithoutPositions.map((lift) => (
                <Paper
                  key={lift.id}
                  elevation={1}
                  sx={{
                    p: 1,
                    cursor: 'grab',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderLeft: 3,
                    borderColor: `${getMaintenanceStatusColor(lift.maintenanceStatus)}.main`,
                  }}
                >
                  <Typography variant="caption" fontWeight={600} display="block">
                    {lift.identifier}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {lift.name}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
